// A train doing something its line does not ordinarily do.
//
// Melbourne runs extra services for events. Six Pakenham trains leave the city
// via Richmond and South Yarra at the end of a match at the Melbourne Cricket
// Ground, and they exist in tight clusters on event dates and on no other day
// — six on a Saturday evening, six around midday on the Friday, none at all
// from Sunday to Thursday. `scripts/fetch-network.mjs` keeps those two stops
// OFF the Pakenham and Cranbourne stop lists on purpose, because a planner that
// read them as line shape would route an ordinary Tuesday trip over a train
// that runs six times a year.
//
// So the bundle is right and the board was silent. The row appeared, correctly
// grouped and correctly coloured, and the app never said that anything about it
// was out of the ordinary.
//
// THE SIGNAL IS THE RUN ITSELF: a train whose stopping pattern calls at a
// station its line does not serve. It comes at two strengths and this module
// answers both.
//
//   * FREE. A board row at a station whose bundled `lines` do not include that
//     row's route. The train is standing at a station its line does not serve,
//     which is already the whole of the observation, and it costs nothing.
//   * CONFIRMED. The run's own pattern against `lineStops`, which NAMES the
//     stations off the line. `DeparturesPage` already fetches patterns for
//     off-line rows to judge destinations, so on the screen where this matters
//     it is usually free as well.
//
// WHAT IT MUST NOT CALL UNUSUAL, and these are the reasons the rule is longer
// than one line:
//
//   * A THROUGH-RUNNER. Since the Metro Tunnel a Pakenham train carries on as a
//     Sunbury one, and its pattern is full of stations the Pakenham line does
//     not serve — Parkville, Arden, Footscray. That is the timetable, not an
//     event, so the through partner's stops count as the line's own. The same
//     goes for the two runs PTV splits such a train into: a stitched pattern is
//     judged against both halves' lines and against the run it came in as.
//   * A V/LINE SERVICE ON A SHARED CORRIDOR. A regional line's stop list in the
//     bundle is its RAILWAY STATIONS as sampled from a handful of real runs,
//     and the same train is published under two routes wherever two regional
//     lines share track — the Warrnambool service is on the Geelong route too,
//     under a different run ref. A stop list built that way cannot bear the
//     weight of "this line does not go there", so no regional route is ever
//     flagged, from either signal.
//   * A CANCELLED ROW. It is not doing anything unusual; it is not doing
//     anything.
//   * A STATION WITH NO LINES RECORDED. A row saved before the app kept line
//     ids is not evidence that every train at it is strange.
//   * A STOP THE BUNDLE CANNOT NAME. Silence rather than "it also calls at
//     1234", which is a sentence that tells nobody anything.
//
// AND IT DOES NOT SAY WHY. The app cannot tell a football special from a train
// diverted round a failure at Caulfield, and the two look identical from here.
// What it observed is what it says.
//
// Pure, and over data already in hand: a board row, a station's line ids, a run
// pattern. No fetch, no store, no Vue — same reason `src/stopping.js` and
// `src/rules.js` are pure, which is that a claim this public has to be testable
// against real bundle data without a browser in the room.

import {
  lineName, lineRecord, lineStops, notCalledAt, routesListedNotCalling, stationById, throughPairs,
} from './network.js';

/** The word on the row. Mono caps, in the vocabulary the platform note is in. */
export const SPECIAL_LABEL = 'SPECIAL';

/** How many stations the sentence names before it starts counting them. */
export const SPECIAL_NAMED = 4;

/** Board rows one plan may read a pattern for, once the free signal has fired. */
export const SPECIAL_CANDIDATES = 2;

/** Routes a plan may add to the board it was already going to ask for. */
export const SPECIAL_PROBE_ROUTES = 4;

// ── Which lines a run is entitled to the stops of ────────────────────────────

/**
 * Every route whose stop list describes where THIS run may legitimately go.
 *
 * Its own, whatever it becomes at the far side of the city, whatever it came in
 * as, and the through partners of all three. A pattern read against its own
 * route alone would call every tunnel train in Melbourne unusual.
 */
function routeFamily(pattern) {
  const ids = new Set();
  const add = (raw) => {
    const id = Number(raw);
    if (!Number.isInteger(id) || id <= 0 || ids.has(id)) return;
    ids.add(id);
    for (const partner of throughPairs()[id] ?? []) {
      const other = Number(partner);
      if (Number.isInteger(other) && other > 0) ids.add(other);
    }
  };
  add(pattern?.routeId);
  // The two runs PTV models one through-running train as, from either end, and
  // under both the names the app has given that fact: `continues` and
  // `stitched` on the far side, `cameFrom` off the server and `fedFrom` off
  // `stitchBefore`.
  add(pattern?.continues?.routeId);
  add(pattern?.stitched?.routeId);
  add(pattern?.cameFrom?.routeId);
  add(pattern?.fedFrom?.routeId);
  return [...ids];
}

/** True where the bundle's stop list for this route is not a claim about shape. */
const unjudgeable = (routeId) => Boolean(lineRecord(routeId)?.regional);

// ── The confirmed signal ─────────────────────────────────────────────────────

/**
 * The stations this run calls at that its line does not serve, in the order it
 * calls at them. Empty for an ordinary train, and empty is the common answer.
 *
 * Station records rather than ids, because every caller wants the name — and a
 * stop the bundle has never heard of is left out rather than named by number.
 */
export function offLineStops(pattern) {
  const routeId = Number(pattern?.routeId);
  if (!Number.isInteger(routeId) || routeId <= 0) return [];
  if (pattern?.cancelled) return [];
  if (unjudgeable(routeId)) return [];
  if (!lineStops(routeId).length) return [];

  const served = new Set();
  for (const id of routeFamily(pattern)) {
    for (const stop of lineStops(id)) served.add(Number(stop.stopId));
  }

  const found = [];
  const seen = new Set();
  for (const stop of pattern?.stops ?? []) {
    const id = Number(stop?.stopId);
    if (!Number.isInteger(id) || served.has(id) || seen.has(id)) continue;
    const station = stationById(id);
    if (!station) continue;
    seen.add(id);
    found.push(station);
  }
  return found;
}

/** Whether the run's own pattern says it is doing something its line does not. */
export function isSpecialRun(pattern) {
  return offLineStops(pattern).length > 0;
}

// ── The free signal ──────────────────────────────────────────────────────────

/**
 * A board row at a station its route does not serve.
 *
 * `serves` is the station's own line ids — `stationLineIds(station.lines)` —
 * and an empty one answers false, because a station that does not know what it
 * serves is not evidence about the trains standing at it.
 *
 * THE ROW'S OTHER LINE COUNTS. A through-running train is signed for the far
 * end of the tunnel and carries `throughRouteId` for the line it becomes; at a
 * station on that half of the journey the row is entirely ordinary, and so is
 * a train on a line that through-runs to one this station serves.
 */
export function offLineRow(departure, serves = []) {
  if (!departure || departure.cancelled) return false;
  const routeId = Number(departure.routeId);
  if (!Number.isInteger(routeId) || routeId <= 0) return false;
  if (unjudgeable(routeId)) return false;

  const here = new Set((Array.isArray(serves) ? serves : [])
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0));
  if (!here.size) return false;

  const family = routeFamily({ routeId, continues: { routeId: departure.throughRouteId } });
  return !family.some((id) => here.has(id));
}

/**
 * Is this row a special: the confirmed answer where there is one, the free one
 * otherwise.
 *
 * THE PATTERN HAS THE LAST WORD, both ways round. It is what names the stations
 * for the sentence, and it is also what withdraws a free hint that turns out to
 * be a through-runner standing on the other line's half of its own journey.
 */
export function isSpecial({ departure = null, serves = [], pattern = null } = {}) {
  if (departure?.cancelled) return false;
  // THE PUBLISHED TIMETABLE HAS THIS TRAIN, and the server says so. Whatever
  // the bundle lists for the station, a scheduled train is not a special.
  if (departure?.scheduled === true) return false;
  if (pattern) return isSpecialRun(pattern);
  return offLineRow(departure, serves);
}

/** The rows on a board worth spending a pattern call on, in board order. */
export function specialRows(rows = [], serves = [], max = SPECIAL_CANDIDATES) {
  return (Array.isArray(rows) ? rows : [])
    .filter((d) => d?.runRef && offLineRow(d, serves))
    .slice(0, max);
}

// ── What to say about it ─────────────────────────────────────────────────────

/** "Richmond and South Yarra", "A, B, C, D and 2 more stations". */
function namesList(names) {
  const rows = (Array.isArray(names) ? names : []).map((n) => String(n ?? '').trim()).filter(Boolean);
  if (!rows.length) return '';
  const shown = rows.slice(0, SPECIAL_NAMED);
  const over = rows.length - shown.length;
  const tail = over > 0 ? `${over} more station${over === 1 ? '' : 's'}` : shown.pop();
  return shown.length ? `${shown.join(', ')} and ${tail}` : tail;
}

/**
 * What was observed, in one sentence, with no guess at the cause in it.
 *
 * "This train is not running the Pakenham line's usual path. It also calls at
 * Richmond and South Yarra." Both halves are facts the app watched happen: the
 * line's path is the bundle's, the extra stops are the run's own. Why it is
 * doing that — an event, a diversion, a driver taking a train back to a depot
 * — is not a question the feed answers, so the sentence does not answer it
 * either.
 *
 * IN ONE PLACE, because the card says it and the journey screen says it, and
 * two copies of a sentence this careful is two things to get out of step.
 */
export function specialPhrase(line, names) {
  const where = namesList(names);
  if (!where) return '';
  const named = String(line ?? '').trim();
  const head = named
    ? `This train is not running the ${named} line’s usual path.`
    : 'This train is not running its line’s usual path.';
  return `${head} It also calls at ${where}.`;
}

/** The same sentence, worked out from a run pattern. Empty for an ordinary train. */
export function specialSentence(pattern) {
  const stops = offLineStops(pattern);
  if (!stops.length) return '';
  return specialPhrase(lineName(pattern?.routeId), stops.map((s) => s.name));
}

// ── Where a special could be picked up ───────────────────────────────────────

/**
 * The routes a trip planner should ADD to the board it was going to ask for
 * anyway, so that a special out of this station would be on it.
 *
 * THIS IS THE WHOLE COST OF THE FEATURE, and it is zero calls. A board request
 * already goes out at the boarding station, narrowed to the routes the plan
 * intends to ride; widening that filter by a route or two is the same one
 * request. And on every ordinary day the widening brings back NOTHING, because
 * the routes named here are by definition the ones with no scheduled service at
 * this stop — so the board is the board it always was, and the thirty-row cap
 * is spent on the same trains.
 *
 * The candidates are the routes PTV lists at the boarding stop and whose
 * ordinary trains do not call there (`notCalledAt`), narrowed to the ones that
 * could finish the journey: the destination is on the line, or is another stop
 * the same correction took off it — which is the Richmond to South Yarra case,
 * where both ends are off the line and one train does the lot.
 *
 * EMPTY AT EVERY OTHER STATION IN THE STATE, which is the point. A trip that
 * starts nowhere near an event platform asks for exactly what it asked for
 * before this module existed.
 *
 * It proposes nothing on its own. Nothing here is a leg, a time or a train; it
 * is a question to put to a live board, and the answer on an ordinary day is no
 * rows at all.
 */
export function probeRoutes(fromStopId, toStopId, { max = SPECIAL_PROBE_ROUTES } = {}) {
  const to = Number(toStopId);
  if (!Number.isInteger(to) || to <= 0) return [];
  return routesListedNotCalling(fromStopId)
    .filter((routeId) => !unjudgeable(routeId))
    .filter((routeId) => lineStops(routeId).some((s) => Number(s.stopId) === to)
      || notCalledAt(routeId).includes(to))
    .slice(0, max);
}
